import React, { useState, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useNavigate } from 'react-router-dom';
import { 
  getAllBlogPosts, 
  saveBlogPost, 
  deleteBlogPost, 
  getBlogImages, 
  uploadBlogImage,
  getBlogImageUrl
} from '../utils/blogService';
import '../styles/admin-blog.css';

const AdminBlogPage = ({ signOut, user }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [editingPost, setEditingPost] = useState(null);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    author: '',
    category: '',
    published: false,
    date: new Date().toISOString().split('T')[0]
  });

  const categories = [
    'Data Preparation', 
    'Conversational Agents', 
    'Predictive Analytics', 
    'Prescriptive AI', 
    'Recommendation Systems', 
    'Computer Vision', 
    'Natural Language Processing', 
    'Custom AI Solutions'
  ];

  useEffect(() => {
    // Check if user is authorized admin
    if (!user) {
      console.log('No user found, waiting for authentication...');
      return;
    }
    
    // Only allow access for your specific email
    const userEmail = user.signInDetails?.loginId || user.username;
    if (userEmail !== 'shanemeister@me.com') {
      console.log('Access denied for user:', userEmail);
      alert(`Access denied. This admin area is restricted to authorized personnel only.`);
      navigate('/');
      return;
    }
    
    console.log('Admin access granted for:', userEmail);
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [blogPosts, blogImages] = await Promise.all([
        getAllBlogPosts(),
        getBlogImages()
      ]);
      setPosts(blogPosts);
      setImages(blogImages);
      
      // Load image URLs
      const urls = {};
      for (const image of blogImages) {
        if (image.path) {
          try {
            const fileName = image.path.split('/').pop();
            urls[image.path] = await getBlogImageUrl(fileName);
          } catch (error) {
            console.error(`Error loading URL for ${image.path}:`, error);
          }
        }
      }
      setImageUrls(urls);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      author: '',
      category: '',
      published: false,
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleEditPost = (post) => {
    console.log('Editing post:', post);
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      content: post.content || '',
      excerpt: post.excerpt || '',
      author: post.author || '',
      category: post.category || '',
      published: post.published || false,
      date: post.date ? post.date.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    console.log('Form data set to:', {
      title: post.title || '',
      content: post.content || '',
      excerpt: post.excerpt || '',
      author: post.author || '',
      category: post.category || '',
      published: post.published || false,
      date: post.date ? post.date.split('T')[0] : new Date().toISOString().split('T')[0]
    });
  };

  const handleSavePost = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const postData = {
        ...formData,
        id: editingPost?.id,
        date: new Date(formData.date).toISOString()
      };
      
      console.log('Saving post data:', postData);

      await saveBlogPost(postData);
      await loadData();
      setEditingPost(null);
      setFormData({
        title: '',
        content: '',
        excerpt: '',
        author: '',
        category: '',
        published: false,
        date: new Date().toISOString().split('T')[0]
      });
      alert('Post saved successfully!');
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error saving post. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (post) => {
    if (window.confirm(`Are you sure you want to delete "${post.title}"?`)) {
      try {
        await deleteBlogPost(post.id);
        await loadData();
        alert('Post deleted successfully!');
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Error deleting post. Please try again.');
      }
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const fileName = `${Date.now()}-${file.name}`;
      await uploadBlogImage(file, fileName);
      await loadData();
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
    }
  };

  const insertImage = (fileName) => {
    const imageTag = `[IMAGE: ${fileName}]`;
    setFormData(prev => ({
      ...prev,
      content: prev.content + `\n\n${imageTag}\n\n`
    }));
    setShowImageSelector(false);
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      try {
        await signOut({ global: true });
        navigate('/');
      } catch (error) {
        console.error('Error signing out:', error);
      }
    }
  };

  if (loading) {
    return <div className="admin-blog-container"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="admin-blog-container">
      <header className="admin-header">
        <div className="header-left">
          <h1>Blog Administration</h1>
        </div>
        <div className="header-right">
          <button onClick={handleNewPost} className="new-post-btn">New Post</button>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <div className="admin-content">
        <div className="posts-list">
          <h2>All Posts</h2>
          {posts.length === 0 ? (
            <p>No posts yet.</p>
          ) : (
            posts.map(post => (
              <div key={post.id} className="post-item">
                <div className="post-info">
                  <h3>{post.title}</h3>
                  <p className="post-meta">
                    {new Date(post.date).toLocaleDateString()} 
                    {post.published ? ' • Published' : ' • Draft'}
                  </p>
                </div>
                <div className="post-actions">
                  <button onClick={() => handleEditPost(post)}>Edit</button>
                  <button onClick={() => handleDeletePost(post)} className="delete-btn">Delete</button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="post-editor">
          <h2>{editingPost ? 'Edit Post' : 'New Post'}</h2>
          
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Post title"
            />
          </div>

          <div className="form-group">
            <label>Author</label>
            <input
              type="text"
              value={formData.author}
              onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
              placeholder="Author name"
            />
          </div>

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Excerpt</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
              placeholder="Brief excerpt for post preview"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Content</label>
            <div className="content-toolbar">
              <button type="button" onClick={() => setShowImageSelector(true)}>Insert Image</button>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ marginLeft: '10px' }}
              />
            </div>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Post content (use [IMAGE: filename] to insert images)"
              rows="15"
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
              />
              Publish immediately
            </label>
          </div>

          <div className="form-actions">
            <button onClick={handleSavePost} disabled={saving} className="save-btn">
              {saving ? 'Saving...' : 'Save Post'}
            </button>
          </div>
        </div>
      </div>

      {showImageSelector && (
        <div className="image-selector-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Select Image</h3>
              <button onClick={() => setShowImageSelector(false)}>×</button>
            </div>
            <div className="images-grid">
              {images.map(image => {
                const fileName = image.path ? image.path.split('/').pop() : 'unknown';
                const imageUrl = imageUrls[image.path] || 'https://via.placeholder.com/150?text=Loading';
                return (
                  <div key={image.path || Math.random()} className="image-item" onClick={() => insertImage(fileName)}>
                    <img src={imageUrl} alt={fileName} />
                    <p>{fileName}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogPage;